import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Brain, TrendingUp, Hash, MessageSquare, ChevronDown, ChevronUp } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell } from "recharts";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const COLORS = {
  primaryDark: "#08263e",
  primaryRed: "#ec474e",
  secondaryBlue: "#518dca",
};

const BAR_COLORS = ["#22c55e", "#3b82f6", "#f59e0b", "#ec474e", "#8b5cf6"];

export const SessionInsightsPanel = ({ token }) => {
  const [insights, setInsights] = useState(null);
  const [expanded, setExpanded] = useState(false);
  const [loading, setLoading] = useState(false);

  const fetchInsights = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/session-insights`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setInsights(res.data);
    } catch {
      // silently fail
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchInsights(); }, [token]);

  if (!insights) return null;

  return (
    <Card className="mt-6" data-testid="session-insights-panel">
      <CardHeader className="pb-3 cursor-pointer" onClick={() => setExpanded(!expanded)}>
        <div className="flex items-center justify-between">
          <CardTitle className="text-lg flex items-center gap-2">
            <Brain size={18} style={{ color: COLORS.secondaryBlue }} />
            Session Memory Insights
          </CardTitle>
          <Button variant="ghost" size="sm" data-testid="session-insights-toggle">
            {expanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
          </Button>
        </div>
      </CardHeader>

      {expanded && (
        <CardContent className="space-y-6">
          {/* Summary Metrics */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <div className="bg-gray-50 rounded-lg p-3 text-center" data-testid="insight-total-sessions">
              <p className="text-2xl font-bold" style={{ color: COLORS.primaryDark }}>{insights.total_sessions}</p>
              <p className="text-xs text-gray-500">Total Sessions</p>
            </div>
            <div className="bg-gray-50 rounded-lg p-3 text-center" data-testid="insight-avg-depth">
              <p className="text-2xl font-bold" style={{ color: COLORS.secondaryBlue }}>{insights.avg_depth}</p>
              <p className="text-xs text-gray-500">Avg Depth (msgs)</p>
            </div>
            <div className="bg-gray-50 rounded-lg p-3 text-center" data-testid="insight-max-depth">
              <p className="text-2xl font-bold" style={{ color: COLORS.primaryRed }}>{insights.max_depth}</p>
              <p className="text-xs text-gray-500">Deepest Session</p>
            </div>
            <div className="bg-gray-50 rounded-lg p-3 text-center" data-testid="insight-summary-count">
              <p className="text-2xl font-bold text-emerald-600">{insights.sessions_with_summary}</p>
              <p className="text-xs text-gray-500">With LLM Summary</p>
            </div>
          </div>

          {/* Conversation Depth Distribution Chart */}
          {insights.depth_distribution?.length > 0 && (
            <div>
              <h4 className="text-sm font-semibold mb-2 flex items-center gap-1.5">
                <MessageSquare size={14} /> Conversation Depth Distribution
              </h4>
              <div className="h-48">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={insights.depth_distribution}>
                    <XAxis dataKey="range" tick={{ fontSize: 11 }} />
                    <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
                    <Tooltip />
                    <Bar dataKey="count" radius={[4, 4, 0, 0]}>
                      {insights.depth_distribution.map((_, i) => (
                        <Cell key={i} fill={BAR_COLORS[i % BAR_COLORS.length]} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          )}

          {/* Fact Extraction Frequency */}
          {Object.keys(insights.fact_frequency || {}).length > 0 && (
            <div>
              <h4 className="text-sm font-semibold mb-2 flex items-center gap-1.5">
                <Hash size={14} /> Fact Extraction Frequency
              </h4>
              <div className="flex flex-wrap gap-2">
                {Object.entries(insights.fact_frequency)
                  .sort((a, b) => b[1] - a[1])
                  .map(([key, count]) => (
                    <span
                      key={key}
                      className="px-2.5 py-1 rounded-full text-xs font-medium"
                      style={{
                        backgroundColor: `${COLORS.secondaryBlue}15`,
                        color: COLORS.primaryDark,
                        border: `1px solid ${COLORS.secondaryBlue}30`,
                      }}
                    >
                      {key.replace(/_/g, " ")} <span className="font-bold ml-1">{count}</span>
                    </span>
                  ))}
              </div>
            </div>
          )}

          {/* Top Extracted Facts */}
          {insights.top_facts?.length > 0 && (
            <div>
              <h4 className="text-sm font-semibold mb-2 flex items-center gap-1.5">
                <TrendingUp size={14} /> Most Common Financial Facts
              </h4>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
                {insights.top_facts.slice(0, 10).map((item, i) => (
                  <div
                    key={i}
                    className="flex items-center justify-between px-3 py-1.5 rounded-md text-xs"
                    style={{ backgroundColor: i < 3 ? `${COLORS.primaryRed}08` : "#f9fafb" }}
                  >
                    <span className="truncate mr-2 font-medium" style={{ color: COLORS.primaryDark }}>
                      {item.fact.replace(/_/g, " ")}
                    </span>
                    <span className="flex-shrink-0 font-bold" style={{ color: i < 3 ? COLORS.primaryRed : COLORS.secondaryBlue }}>
                      {item.count}x
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {insights.total_sessions === 0 && (
            <p className="text-sm text-gray-400 text-center py-4">No session data yet. Insights will appear as users chat.</p>
          )}
        </CardContent>
      )}
    </Card>
  );
};
